'use client';

import { useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslation, Text, Icon, useUi, Link } from '@weareconceptstudio/core';
import gsap from 'gsap/all';

import { HEADER_BANNER_MARQUEE_MAX_WIDTH, HEADER_BANNER_READY_EVENT } from '@/constants';
import BannerHeaderStyle from './style';

const BANNER_CLIP_COLLAPSED = 'inset(0 0 100% 0 round 0px)';
const BANNER_CLIP_EXPANDED = 'inset(0 0 0% 0 round 0px)';

const dispatchBannerReady = () => {
	window.dispatchEvent(new Event(HEADER_BANNER_READY_EVENT));
};

const BannerHeader = ({ text, phone, loadAnimation, isMobileBanner = false }) => {
	const { selectedLang } = useTranslation();
	const { winWidth } = useUi();
	const pathname = usePathname();
	const effectiveWidth = typeof window !== 'undefined' ? window.innerWidth : winWidth;
	const animatedRef = useRef(false);
	const isMarqueeBanner = useMemo(
		() => isMobileBanner && effectiveWidth <= HEADER_BANNER_MARQUEE_MAX_WIDTH,
		[isMobileBanner, effectiveWidth]
	);
	const contentRef = useRef();
	const bannerRef = useRef();

	useLayoutEffect(() => {
		const wrapper = bannerRef.current?.querySelector('.wrapper-contact');
		if (!wrapper) return;

		gsap.set(wrapper, {
			clipPath: animatedRef.current ? BANNER_CLIP_EXPANDED : BANNER_CLIP_COLLAPSED,
		});
	}, [pathname]);

	useEffect(() => {
		const el = contentRef.current;
		if (!el || !isMarqueeBanner) return;

		const slow = () => {
			el.getAnimations()[0]?.updatePlaybackRate(0.3);
		};
		const restore = () => {
			el.getAnimations()[0]?.updatePlaybackRate(1);
		};

		el.addEventListener('touchstart', slow, { passive: true });
		el.addEventListener('touchend', restore, { passive: true });
		el.addEventListener('touchcancel', restore, { passive: true });

		return () => {
			el.removeEventListener('touchstart', slow);
			el.removeEventListener('touchend', restore);
			el.removeEventListener('touchcancel', restore);
		};
	}, [isMarqueeBanner]);

	useEffect(() => {
		const wrapper = bannerRef.current?.querySelector('.wrapper-contact');
		const headerContainer = bannerRef.current?.closest('header')?.querySelector('.header-container');

		const isHomePage = pathname.replace(`${selectedLang}/`, '') === '/' || pathname === '/';
		if (!loadAnimation && isHomePage) return;

		if (animatedRef.current) return;
		animatedRef.current = true;

		if (wrapper) {
			gsap.to(wrapper, {
				clipPath: BANNER_CLIP_EXPANDED,
				duration: 0.6,
				ease: 'power1.out',
				delay: 0.4,
				onComplete: dispatchBannerReady,
			});
		}

		if (headerContainer) {
			gsap.fromTo(headerContainer, { opacity: 0 }, { opacity: 1, duration: 1.2, ease: 'power1.out' });
		}
	}, [pathname, loadAnimation, selectedLang]);

	return (
		<BannerHeaderStyle ref={bannerRef}>
			<div className='wrapper-contact'>
				<div
					ref={contentRef}
					className='wrapper-inner-content'>
					{Array.from({ length: isMarqueeBanner ? 2 : 1 }).map((_, i) => (
						<div
							key={i}
							className='wrapper-content-contact'>
							<Text className='p p5 font-montserrat-arm-medium align-center'>
								{text.title}
								&nbsp;
								{text.text || null}
							</Text>
							<div className='line-horizontal' />
							<Link
								external
								url={`tel:${phone[0]}`}
								className='phone'>
								<Icon
									className='phone-icon font-montserrat-arm-medium'
									name='phone'
								/>
								<Text className='p p5 font-montserrat-arm-medium phone-text'>{phone[0]}</Text>
							</Link>
						</div>
					))}
				</div>
			</div>
		</BannerHeaderStyle>
	);
};

export default BannerHeader;
